import { useQueryClient } from '@tanstack/react-query';
import { LogOut, Info, Section, Settings, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useLogout } from '@/features/auth/hooks/use-logout';
import { useCanManageOrgMembers } from '@/features/org/hooks/use-can-manage-org-members';
import { useOrgUser } from '@/features/org/hooks/use-org-user';
import { getOrgRoleDisplay } from '@/features/org/utils/type-utils';
import { OrgRole } from '@/features/org/validation/org-role.schema';
import { useUserProfile } from '@/features/profile/hooks/use-user-profile';
import { useCanManageSubscriptions } from '@/features/subscriptions/hooks/use-can-manage-subscriptions';
import SyncaraLogo from '@/shared/assets/syncara.png';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import { getInitials } from '@/shared/utils/avatar-util';
import {
  buildSupportEmailUrl,
  SupportEmailTranslations,
} from '@/shared/utils/support-email-helper';

export const CurrentUserButton = () => {
  const { data: orgUser } = useOrgUser();
  const { data: userProfile } = useUserProfile();
  const logout = useLogout();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const canManageMembers = useCanManageOrgMembers();
  const canManageSubscriptions = useCanManageSubscriptions();
  const { t } = useTranslation('common', { keyPrefix: 'navbar' });
  const { t: tSupport } = useTranslation('common', {
    keyPrefix: 'support_email',
  });
  const { t: tRoles } = useTranslation('org', { keyPrefix: 'Roles' });

  const avatarUrl = userProfile?.avatar_url;
  const displayName = userProfile?.name || t('profile');
  const userEmail = userProfile?.email || '';
  const initials = getInitials(displayName);

  const handleHelpClick = () => {
    const translations: SupportEmailTranslations = {
      unknown: tSupport('unknown'),
      subject: tSupport('subject'),
      greeting: tSupport('greeting'),
      intro: tSupport('intro'),
      section1_title: tSupport('section1_title'),
      user_email_label: tSupport('user_email_label'),
      name_label: tSupport('name_label'),
      feature_label: tSupport('feature_label'),
      timestamp_label: tSupport('timestamp_label'),
      section2_title: tSupport('section2_title'),
      problem_question: tSupport('problem_question'),
      problem_placeholder: tSupport('problem_placeholder'),
      steps_title: tSupport('steps_title'),
      step_placeholder: tSupport('step_placeholder'),
      section3_title: tSupport('section3_title'),
      os_label: tSupport('os_label'),
      browser_label: tSupport('browser_label'),
      screen_label: tSupport('screen_label'),
      error_label: tSupport('error_label'),
      error_placeholder: tSupport('error_placeholder'),
      screenshot_label: tSupport('screenshot_label'),
      screenshot_placeholder: tSupport('screenshot_placeholder'),
      thanks: tSupport('thanks'),
      regards: tSupport('regards'),
    };

    const mailtoUrl = buildSupportEmailUrl(
      userEmail,
      displayName,
      location.pathname,
      translations,
    );
    window.open(mailtoUrl);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar size={'lg'} className="cursor-pointer rounded-lg">
          <AvatarImage src={avatarUrl || ''} alt={displayName} />
          <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 p-2"
        side={'right'}
        align="end"
        sideOffset={28}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-3 px-1 py-1.5 text-left text-sm">
            <Avatar
              size={'lg'}
              className="rounded-lg border-[1.25px] border-white"
            >
              <AvatarImage src={avatarUrl || ''} alt={displayName} />
              <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{displayName}</span>
              <span className="truncate text-xs">
                {getOrgRoleDisplay(orgUser?.role as OrgRole, (key) =>
                  tRoles(key as any),
                )}
              </span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuGroup className="py-6">
          {canManageSubscriptions && (
            <MenuItem
              icon={
                <img
                  className="size-5 object-contain"
                  src={SyncaraLogo}
                  alt="Syncara Logo"
                />
              }
              label={t('plans_management')}
              className="mb-[14px] ml-0.5"
              onClick={() => navigate(paths.plansManagement.getHref())}
            />
          )}
          <MenuItem
            icon={<Settings />}
            label={t('user_menu.settings')}
            className="mb-[14px]"
            onClick={() => {
              navigate(paths.settings.getHref());
            }}
          />
          {canManageMembers && (
            <MenuItem
              icon={<Users />}
              label={t('user_menu.member_management')}
              className="mb-[14px]"
              onClick={() => {
                navigate(paths.org.memberManagement.getHref());
              }}
            />
          )}
          <MenuItem
            icon={<Section />}
            label={t('user_menu.legal')}
            className="mb-[14px]"
            onClick={() => {
              window.open('https://syncara.io', '_blank');
            }}
          />
          <MenuItem
            icon={<Info />}
            label={t('user_menu.help')}
            onClick={handleHelpClick}
          />
        </DropdownMenuGroup>
        <MenuItem
          icon={<LogOut />}
          label={t('user_menu.logout')}
          onClick={async () => {
            await logout.mutateAsync({});
            queryClient.clear();
            window.location.href = paths.auth.login.getHref();
          }}
          className="!text-error-red hover:!text-error-red"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  asChild?: boolean;
  children?: React.ReactNode;
  className?: string;
}

const MenuItem = ({
  icon,
  label,
  onClick,
  asChild,
  children,
  className,
}: MenuItemProps) => {
  return (
    <DropdownMenuItem onClick={onClick} asChild={asChild} className={className}>
      {children || (
        <>
          <span className="mr-2">{icon}</span>
          {label}
        </>
      )}
    </DropdownMenuItem>
  );
};
